import { api as apiConfig } from '../../config';

/**
 * Simple utility function to promise-ify a timeout
 *
 * @param {Function} fn the function to run later
 * @param {integer} delay milliseconds before function execution
 */
var later = function later(fn, delay) {
  return new Promise(function (resolve, reject) {
    window.setTimeout(function () {
      return fn().then(resolve).catch(reject);
    }, delay);
  });
};

/**
 * Tests an error to see if it should be retried here.
 *
 * Currently only allows retries in the specific case of timeouts.
 *
 * @param {object} err an error object as returned from axios
 */
var shouldRetry = function shouldRetry(err) {
  var isAbortError = err.code === 'ECONNABORTED';
  var suspectedTimeout = err.isAxiosError && !err.response && !err.status;
  return isAbortError || suspectedTimeout;
};

/**
 * Recursive function to repeat a request until timeouts stop happening or we
 * reach the RETRY_LIMIT value.
 *
 * Will trigger an HTTP request after the delay passes. Should that resolve, the
 * resulting promise will be returned. However, should it fail the attempt will
 * instead be repeated.
 *
 * Should a non-timeout error occur in one of the repeat requests (e.g 404 or
 * 500) this will be taken as "success", because we successfully got through to
 * the API, and the resulting error will be returned as a promise rejection
 *
 * @param {object} config The request configuration
 * @param {integer} delay The delay until the next attempt
 * @param {integer} retries The number of retries so far
 */
var repeatingRequest = function repeatingRequest(axios, config, delay, retries) {
  return function (err) {
    // don't make the request if we've tried too many times already
    // or this is a server-side failure
    if (retries >= apiConfig.retryLimit || !shouldRetry(err)) {
      return Promise.reject(err);
    }
    var retryFn = repeatingRequest(axios, config, delay * apiConfig.delayMultiplier, retries + 1);
    return later(function () {
      return axios({
        useBackoff: false
      }).then(function (instance) {
        return instance.request(config);
      }).catch(retryFn);
    }, delay);
  };
};

/**
 * Checks to see if the error is a timeout, and if so makes repeating calls
 * until timeouts cease, or the RETRY_LIMIT is reached.
 *
 * The first call will be made 2 seconds after this function is triggered, with
 * each subsequent call waiting twice as long as the previous before firing.
 *
 * @param {object} err an axios error
 */
var exponentialBackoff = function exponentialBackoff(axiosInstance) {
  return function (err) {
    var config = err.config;
    var retryFn = repeatingRequest(axiosInstance, config, apiConfig.backoffDelay, 0);
    return retryFn(err);
  };
};
export default exponentialBackoff;